import { CARD_TERMS_DRAFT } from '@orn/global/billing/mandateText.js';
import Button from '@orn/shared-web/components/Button/Button';

import { interpolateMandate } from '../MandateAuthorization/interpolateMandate';

export interface ICardAuthorizationBaseProps {
  /** Server-formatted, rendered verbatim — the same strings the evidence row stores. */
  amountDisplay: string;
  firstChargeDisplay: string;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  /** Typed refusal text, already mapped to something a subscriber can act on. */
  errorMessage?: string;
}

/**
 * The card rail's final consent screen — the recurring-charge authorization.
 *
 * The shared authorize screen with the payment-method block swapped: the card
 * was already entered and confirmed earlier on this rail, so this names no
 * account and shows no owner-name disclosure — that check reads a bank
 * account, and there is none here. The consent text renders from the card
 * registry's copy, so the version stored on the evidence row names what was
 * actually shown.
 */
const CardAuthorizationBase = ({
  amountDisplay,
  firstChargeDisplay,
  onSubmit,
  onBack,
  isSubmitting,
  errorMessage,
}: ICardAuthorizationBaseProps) => (
  <div className="flex w-full flex-col gap-6">
    <div className="border-secondary flex w-full flex-col gap-1 rounded-xl border p-4">
      <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">Paying by card</p>
      <p className="text-primary text-md font-medium">The card you just entered</p>
      <p className="text-tertiary text-sm">
        $0 today. {amountDisplay} starting {firstChargeDisplay}.
      </p>
    </div>

    <p className="text-tertiary text-xs">
      {interpolateMandate(CARD_TERMS_DRAFT, { amount: amountDisplay, firstChargeDate: firstChargeDisplay })}
    </p>

    {errorMessage !== undefined && <p className="text-error-primary text-sm">{errorMessage}</p>}

    <Button type="button" size="xl" color="primary" className="w-full" onClick={onSubmit} isLoading={isSubmitting}>
      Start free trial
    </Button>

    <Button type="button" size="md" color="link-gray" className="w-full" onClick={onBack} isDisabled={isSubmitting}>
      Back
    </Button>
  </div>
);

CardAuthorizationBase.displayName = 'CardAuthorizationBase';

export default CardAuthorizationBase;
